/**
 * StageMovePromptView — the reason prompt shown before a card enters a stage
 * whose rule requires one (SE-5495).
 *
 * Board-local rather than the app-global `CustomPrompt` singleton (mounted once
 * in `src/app/_layout.tsx` and threaded as `promptRef`): that one is global and
 * text-only, with no room for the outcome selector SE-5496 adds here. Rendering
 * off the `{ requiresRationale, requiresOutcome }` gate means that ticket slots
 * a field in beside the reason instead of replacing this component.
 *
 * Built on `DesktopModal` specifically for its `closeOnBackdrop` opt-out. A
 * reason prompt holds text the user just typed AND gates a pending card move,
 * so a stray outside click must not silently discard both — the same hazard
 * `CreateWatchlistModal` handles with its onClose/onBackdropDismiss split. Here
 * the backdrop and ESC are simply inert; dismissal is an explicit Cancel.
 *
 * Desktop-only chrome is deliberate and matches the ticket's scope: the entry
 * point is a @dnd-kit board drag, which renders HTML divs and is web-only. When
 * SE-5494 brings the player drawer in, this is where a `MobileSheetChrome`
 * branch (gated on `useMobileScreen()`, never on platform) belongs.
 *
 * Pure presentation — every value is resolved by `useStageMovePrompt`, with the
 * per-outcome press handlers and the reason-field gate bound by
 * `StageMovePromptContainer` (SE-8019).
 * @module features/player-kanban/components/molecules/StageMovePrompt/StageMovePromptView
 */

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import React, { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { Textarea, TextareaInput } from "@/components/atoms/Textarea";
import DesktopModal from "@/components/molecules/DesktopModal";
import type { KanbanCardOutcome } from "@/generated/graphql";

import { outcomeLabel } from "../../../utils/outcomeLabels";

/** Cap on the recorded reason, mirroring the rename-watchlist convention. */
export const STAGE_MOVE_REASON_MAX_LENGTH = 280;

/**
 * Props for {@link StageMovePromptView} — supplied verbatim by
 * `useStageMovePrompt`.
 */
export interface StageMovePromptViewProps {
  /** Whether the prompt is visible. */
  readonly isOpen: boolean;
  /** Player being moved. */
  readonly playerName?: string;
  /** Destination stage name. */
  readonly toPhaseName?: string;
  /** Origin stage name. */
  readonly fromPhaseName?: string;
  /** Whether a terminal outcome is required (SE-5496). */
  readonly requiresOutcome: boolean;
  /** SE-5496: the board template's ordered valid outcomes to choose from. */
  readonly validOutcomes: readonly KanbanCardOutcome[];
  /** SE-5496: the currently-selected outcome, if any. */
  readonly selectedOutcome?: KanbanCardOutcome;
  /**
   * SE-5496: press handler per outcome, pre-bound by the Container so the JSX
   * passes a plain reference (react-perf/jsx-no-new-function-as-prop forbids
   * inline closures in attributes).
   */
  readonly outcomeHandlers: Record<KanbanCardOutcome, () => void>;
  /** Current reason text. */
  readonly rationale: string;
  /** Reason text change handler. */
  readonly onChangeRationale: (value: string) => void;
  /** Whether confirm is blocked by an unmet gate. */
  readonly isSubmitDisabled: boolean;
  /**
   * Whether the reason field is shown. SE-5496: a terminal close requires a
   * reason as well, so it appears when a rationale OR an outcome is required.
   */
  readonly showReason: boolean;
  /** Confirm handler. */
  readonly onSubmit: () => void;
  /** Cancel handler — abandons the pending move. */
  readonly onCancel: () => void;
}

/**
 * Builds the "Player → Stage" body line, degrading gracefully when either name
 * is unknown (a card whose player failed to resolve must still be movable).
 * @param playerName - Player being moved.
 * @param toPhaseName - Destination stage name.
 * @returns A human-readable sentence describing the move.
 */
const buildMoveSummary = (
  playerName?: string,
  toPhaseName?: string
): string => {
  const who = playerName ?? "This player";
  return toPhaseName
    ? `${who} is moving into ${toPhaseName}.`
    : `${who} is moving to a new stage.`;
};

/**
 * Renders the sensitive-stage reason prompt, extended with the terminal-outcome
 * selector (SE-5496).
 * @param props - View props (see {@link StageMovePromptViewProps})
 * @param props.isOpen - Whether the prompt is visible
 * @param props.playerName - Player being moved
 * @param props.toPhaseName - Destination stage name
 * @param props.fromPhaseName - Origin stage name
 * @param props.requiresOutcome - Whether an outcome is required (SE-5496)
 * @param props.validOutcomes - Ordered valid outcomes for the selector
 * @param props.selectedOutcome - Currently-selected outcome
 * @param props.outcomeHandlers - Pre-bound press handler per outcome
 * @param props.rationale - Current reason text
 * @param props.onChangeRationale - Reason text change handler
 * @param props.isSubmitDisabled - Whether confirm is blocked
 * @param props.showReason - Whether the reason field is shown
 * @param props.onSubmit - Confirm handler
 * @param props.onCancel - Cancel handler
 * @returns The rendered prompt modal, or null when closed.
 */
const StageMovePromptView = ({
  isOpen,
  playerName,
  toPhaseName,
  fromPhaseName,
  requiresOutcome,
  validOutcomes,
  selectedOutcome,
  outcomeHandlers,
  rationale,
  onChangeRationale,
  isSubmitDisabled,
  showReason,
  onSubmit,
  onCancel,
}: StageMovePromptViewProps) =>
  // Rendering nothing while closed keeps a dormant prompt out of the
  // accessibility tree and guarantees the field remounts empty for each new
  // move, without a reset effect (the repo's `set-state-in-effect` rule).
  isOpen ? (
    <DesktopModal
      isOpen
      onClose={onCancel}
      title={requiresOutcome ? "Close this card" : "Add a reason for this move"}
      width="md"
      // A pending card move plus freshly-typed text is exactly the state the
      // DesktopModal docs say to protect: dismissal must be deliberate, so an
      // outside click or ESC cannot discard the reason and drop the move.
      closeOnBackdrop={false}
      closeOnEsc={false}
      footer={
        <Stack direction="horizontal" className="w-full justify-end gap-2">
          <Button
            testID={TEST_ID_PLAYER_KANBAN.STAGE_MOVE_PROMPT_CANCEL}
            variant="outline"
            tone="secondary"
            onPress={onCancel}
            label="Cancel"
          />
          <Button
            testID={TEST_ID_PLAYER_KANBAN.STAGE_MOVE_PROMPT_SUBMIT}
            tone="primary"
            onPress={onSubmit}
            isDisabled={isSubmitDisabled}
            label={requiresOutcome ? "Close card" : "Move player"}
          />
        </Stack>
      }
    >
      <Stack
        className="w-full gap-3"
        testID={TEST_ID_PLAYER_KANBAN.STAGE_MOVE_PROMPT}
        {...{ "data-testid": TEST_ID_PLAYER_KANBAN.STAGE_MOVE_PROMPT }}
      >
        <Text variant="body" className="text-content-secondary">
          {buildMoveSummary(playerName, toPhaseName)}
        </Text>
        <Text variant="caption" className="text-content-muted">
          {fromPhaseName
            ? `Moving from ${fromPhaseName}. This reason is recorded on the pipeline activity log.`
            : "This reason is recorded on the pipeline activity log."}
        </Text>
        {requiresOutcome ? (
          <Stack className="w-full gap-2">
            <Text variant="body-strong" className="text-content-primary">
              Outcome
            </Text>
            <Stack
              direction="horizontal"
              className="w-full flex-wrap gap-2"
              testID={TEST_ID_PLAYER_KANBAN.STAGE_MOVE_PROMPT_OUTCOMES}
              {...{
                "data-testid": TEST_ID_PLAYER_KANBAN.STAGE_MOVE_PROMPT_OUTCOMES,
              }}
            >
              {validOutcomes.map(outcome => (
                <Button
                  key={outcome}
                  testID={TEST_ID_PLAYER_KANBAN.stageMoveOutcomeOptionFor(
                    outcome
                  )}
                  variant={selectedOutcome === outcome ? "solid" : "outline"}
                  tone={selectedOutcome === outcome ? "primary" : "secondary"}
                  size="sm"
                  onPress={outcomeHandlers[outcome]}
                  label={outcomeLabel(outcome)}
                />
              ))}
            </Stack>
          </Stack>
        ) : null}
        {showReason ? (
          <Textarea size="md" className="min-h-24">
            <TextareaInput
              testID={TEST_ID_PLAYER_KANBAN.STAGE_MOVE_PROMPT_INPUT}
              accessibilityLabel="Reason for this move"
              value={rationale}
              onChangeText={onChangeRationale}
              placeholder="Why is this player moving here?"
              maxLength={STAGE_MOVE_REASON_MAX_LENGTH}
            />
          </Textarea>
        ) : null}
      </Stack>
    </DesktopModal>
  ) : null;

StageMovePromptView.displayName = "StageMovePromptView";

export default memo(StageMovePromptView);
